import { StorageKeys, StorageService } from 'src/services/storage.service';
import { create } from 'zustand';

import { UserRole } from '@/types/user-role';

export type ViewContext = 'site' | 'tenant';

interface UserStore {
  siteToken: string | null;
  setSiteToken: (token: string) => void;
  clearSiteToken: () => void;

  organizationToken: string | null;
  setOrganizationToken: (token: string) => void;
  clearOrganizationToken: () => void;

  userId: string | null;
  setUserId: (userId: string) => void;
  clearUserId: () => void;

  userRole: UserRole | null;
  setUserRole: (userRole: UserRole) => void;
  clearUserRole: () => void;

  isSSO: boolean;
  setIsSSO: (isSSO: boolean) => void;
  clearIsSSO: () => void;

  isLoadingViewContextAccess: boolean;
  setIsLoadingViewContextAccess: (isLoading: boolean) => void;
}

export const useUserStore = create<UserStore>(set => ({
  userId: StorageService.getItem(StorageKeys.USER_ID) ?? null,
  setUserId: (userId: string) =>
    set(() => {
      StorageService.setItem(StorageKeys.USER_ID, userId);

      return {
        userId,
      };
    }),
  clearUserId: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.USER_ID);
      return {
        userId: null,
      };
    }),

  userRole: (StorageService.getItem(StorageKeys.USER_ROLE) as UserRole) ?? null,
  setUserRole: (userRole: UserRole) =>
    set(() => {
      StorageService.setItem(StorageKeys.USER_ROLE, userRole);

      return {
        userRole,
      };
    }),
  clearUserRole: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.USER_ROLE);
      return {
        userRole: null,
      };
    }),

  isSSO: false,
  setIsSSO: isSSO => set({ isSSO }),
  clearIsSSO: () => set({ isSSO: false }),

  siteToken: StorageService.getItem(StorageKeys.SITE_TOKEN) ?? null,
  setSiteToken: siteToken =>
    set(() => {
      StorageService.setItem(StorageKeys.SITE_TOKEN, siteToken);

      return {
        siteToken,
      };
    }),
  clearSiteToken: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.SITE_TOKEN);
      return { siteToken: null };
    }),

  organizationToken: StorageService.getItem(StorageKeys.ORGANIZATION_TOKEN) ?? null,
  setOrganizationToken: organizationToken =>
    set(() => {
      StorageService.setItem(StorageKeys.ORGANIZATION_TOKEN, organizationToken);

      return {
        organizationToken,
      };
    }),
  clearOrganizationToken: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.ORGANIZATION_TOKEN);
      return { organizationToken: null };
    }),

  isLoadingViewContextAccess: true,
  setIsLoadingViewContextAccess: (isLoadingViewContextAccess: boolean) =>
    set(() => ({ isLoadingViewContextAccess })),
}));
